/**
 * Sales > Executive Summary — headline tiles, top brands vs year ago, momentum
 * across periods, category / segment mix, and the biggest movers.
 */

import { useMemo } from 'react'
import type { EChartsOption } from 'echarts'

import Chart from '../../components/Chart'
import { DataTable } from '../../components/DataTable'
import { formatCompact } from '../../lib/format'
import type { CustomReportProps } from '../index'
import {
  BAD,
  GOOD,
  chart,
  compactAxis,
  money,
  num,
  colorAt,
  colorsFor,
  pct,
  pctAxis,
  records,
  signedPct,
  signedPts,
  toneOf,
} from './shared'
import { Empty, Panel, Tile } from './ui'

const PERIOD_LABEL: Record<string, string> = {
  '04W': '4 wks',
  '13W': '13 wks',
  '26W': '26 wks',
  '52W': '52 wks',
  YTD: 'YTD',
}

export function ExecutiveSummary({ result, department, values }: CustomReportProps) {
  const focus = typeof values.focus_brand === 'string' ? values.focus_brand : 'none'
  const hasFocus = focus !== 'none'
  const { accent, second: ya, third } = colorsFor(department)
  const kpi = records(result, 'kpis')[0]
  const brands = records(result)
  const momentum = records(result, 'momentum')
  const mix = records(result, 'mix')
  const movers = records(result, 'movers')

  const brandOption = useMemo<EChartsOption>(() => {
    const top = [...brands].reverse()
    return {
      ...chart.baseOption(),
      grid: { left: 8, right: 56, top: 8, bottom: 28, containLabel: true },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: {
        type: 'value',
        axisLabel: { ...chart.axisLabel(), formatter: compactAxis },
        splitLine: chart.splitLine(),
      },
      yAxis: {
        type: 'category',
        data: top.map((b) => String(b.brand)),
        axisLabel: { ...chart.axisLabel(), fontFamily: 'IBM Plex Sans, sans-serif' },
        axisLine: chart.axisLine(),
        axisTick: { show: false },
      },
      series: [
        {
          name: 'Year ago',
          type: 'bar',
          data: top.map((b) => num(b.dollar_sales_ya)),
          itemStyle: { color: ya, opacity: 0.35 },
          barGap: '-100%',
          barMaxWidth: 18,
        },
        {
          name: 'This year',
          type: 'bar',
          data: top.map((b) => ({
            value: num(b.dollar_sales),
            itemStyle: { color: b.is_focus === 'Yes' ? third : accent },
          })),
          barMaxWidth: 12,
          label: {
            show: true,
            position: 'right',
            fontSize: 11,
            formatter: (p: { dataIndex: number }) => signedPct(top[p.dataIndex]?.dollar_chg ?? null, 0),
          },
        },
      ],
    }
  }, [brands, accent, ya, third])

  const momentumOption = useMemo<EChartsOption>(
    () => ({
      ...chart.baseOption(),
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: unknown) => (typeof v === 'number' ? signedPct(v) : '—'),
      },
      xAxis: {
        type: 'category',
        data: momentum.map((m) => PERIOD_LABEL[String(m.period)] ?? String(m.period)),
        axisLabel: chart.axisLabel(),
        axisLine: chart.axisLine(),
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { ...chart.axisLabel(), formatter: pctAxis },
        splitLine: chart.splitLine(),
      },
      series: [
        {
          name: 'Category',
          type: 'bar',
          barMaxWidth: 28,
          data: momentum.map((m) => ({
            value: num(m.category_chg),
            itemStyle: { color: (num(m.category_chg) ?? 0) >= 0 ? accent : BAD },
          })),
        },
        ...(hasFocus
          ? [
              {
                name: focus,
                type: 'bar' as const,
                barMaxWidth: 28,
                itemStyle: { color: third },
                data: momentum.map((m) => num(m.focus_chg)),
              },
            ]
          : []),
      ],
    }),
    [momentum, accent, third, hasFocus, focus],
  )

  const mixOption = useMemo<EChartsOption>(() => {
    const colors = colorsFor(department).list(mix.length)
    return {
      ...chart.baseOption(),
      tooltip: { trigger: 'item', formatter: '{b}: {d}%' },
      legend: { type: 'scroll' as const, orient: 'vertical', right: 0, top: 'middle', textStyle: { fontSize: 11 } },
      series: [
        {
          type: 'pie',
          radius: ['48%', '78%'],
          center: ['36%', '50%'],
          avoidLabelOverlap: true,
          label: { show: false },
          itemStyle: { borderColor: 'transparent', borderWidth: 2 },
          data: mix.map((m, i) => ({
            name: String(m.label),
            value: num(m.dollar_sales) ?? 0,
            itemStyle: { color: colorAt(colors, i) },
          })),
        },
      ],
    }
  }, [mix, department])

  const moversOption = useMemo<EChartsOption>(() => {
    const ordered = [...movers].sort((a, b) => (num(a.change_dollars) ?? 0) - (num(b.change_dollars) ?? 0))
    return {
      ...chart.baseOption(),
      grid: { left: 8, right: 16, top: 8, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v: unknown) => (typeof v === 'number' ? money(v) : '—'),
      },
      legend: { show: false },
      xAxis: {
        type: 'value',
        axisLabel: { ...chart.axisLabel(), formatter: compactAxis },
        splitLine: chart.splitLine(),
      },
      yAxis: {
        type: 'category',
        data: ordered.map((m) => String(m.brand)),
        axisLabel: { ...chart.axisLabel(), fontFamily: 'IBM Plex Sans, sans-serif' },
        axisTick: { show: false },
        axisLine: chart.axisLine(),
      },
      series: [
        {
          name: '$ change vs YA',
          type: 'bar',
          barMaxWidth: 16,
          data: ordered.map((m) => ({
            value: num(m.change_dollars),
            itemStyle: { color: (num(m.change_dollars) ?? 0) >= 0 ? GOOD : BAD },
          })),
        },
      ],
    }
  }, [movers])

  if (kpi === undefined || num(kpi.category_sales) === null) {
    return <Empty>No sales for these filters.</Empty>
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Dollar sales"
          value={money(kpi.category_sales)}
          delta={`${signedPct(kpi.category_chg)} vs YA`}
          deltaTone={toneOf(kpi.category_chg)}
        />
        <Tile
          label="Unit sales"
          value={formatCompact(num(kpi.units), 2)}
          delta={`${signedPct(kpi.units_chg)} vs YA`}
          deltaTone={toneOf(kpi.units_chg)}
        />
        {hasFocus ? (
          <>
            <Tile
              label={`${focus} sales`}
              value={money(kpi.focus_sales)}
              delta={`${signedPct(kpi.focus_chg)} vs YA`}
              deltaTone={toneOf(kpi.focus_chg)}
            />
            <Tile
              label={`${focus} share`}
              value={pct(kpi.focus_share)}
              delta={signedPts(kpi.focus_share_chg_pts)}
              deltaTone={toneOf(kpi.focus_share_chg_pts)}
              note="vs YA"
            />
          </>
        ) : (
          <>
            <Tile label="Brands selling" value={String(num(kpi.brands) ?? '—')} />
            <Tile label="Items selling" value={String(num(kpi.items) ?? '—')} />
          </>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Panel
          title="Top brands"
          subtitle={`Dollar sales vs year ago (faded bar); label is % change${hasFocus ? `. ${focus} highlighted.` : '.'}`}
        >
          <Chart option={brandOption} height={340} ariaLabel="Top brands by dollar sales" />
        </Panel>
        <Panel
          title="Momentum"
          subtitle="Dollar sales % change vs year ago over each rolling window"
        >
          <Chart option={momentumOption} height={340} ariaLabel="Percent change by period" />
        </Panel>
        <Panel
          title={values.category === 'all' ? 'Category mix' : 'Segment mix (Attribute 1)'}
          subtitle="Share of dollar sales"
        >
          {mix.length === 0 ? (
            <Empty>No data.</Empty>
          ) : (
            <Chart option={mixOption} height={300} ariaLabel="Sales mix" />
          )}
        </Panel>
        <Panel title="Biggest movers" subtitle="Brands with the largest dollar gain and loss vs year ago">
          {movers.length === 0 ? (
            <Empty>No movers.</Empty>
          ) : (
            <Chart option={moversOption} height={300} ariaLabel="Biggest brand movers" />
          )}
        </Panel>
      </div>

      <DataTable columns={result.columns} rows={result.rows} caption="Top brands" />
    </div>
  )
}
